import {
  ContentItem,
  ContentItems,
  ContentWrapper,
  Flex,
  Text,
  TitledText,
} from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { useExpenditureBudgetPurpose } from '@/features/master-table/hooks';
import { getExpenditureBudgetPurposeConcatData } from '@/features/mold/utils';
import {
  ReviewSheetModel,
  SkipAdministrativeReformTeamEvaluationReasonLabelModifier,
} from '@/features/project/projects/_models/project';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { ProjectInformationHeader } from './ProjectInformationHeader';
import {
  ProjectCategoryPinpointItem,
  ProjectFiscalYearPinpointItem,
  ProjectGroupPinpointItem,
  ProjectNumberPinpointItem,
  ProjectOldProjectNumberPinpointItem,
  ProjectOrderNumberPinpointItem,
  ProjectPeriodPinpointItem,
  ProjectPolicyPinpointItem,
  ProjectRemarkPinpointItem,
  ProjectSheetTypePinpointItem,
} from './pinpoint-items/basic-information-pinpoint-fields';

type Props = z.infer<typeof ReviewSheetModel> & {
  historySavedAt: string | undefined;
};

export const RsSkippedReviewProjectInformation: FC<Props> = (props) => {
  const {
    id,
    fiscalYear,
    projectNumber,
    oldProjectNumber,
    startYear,
    endYear,
    isUnknownStart,
    isNoScheduledEnd,
    category,
    purpose,
    overview,
    overviewUrl,
    policiesForProject,
    task,
    mainCosts,
    remark,
    skipAdministrativeReformTeamEvaluationReason,
    skipAdministrativeReformTeamEvaluationReasonSupplement,
    sortOrder,
    name,
    majorExpenditureBudgetPurposeId,
    sheetType,
    ...rest
  } = ReviewSheetModel.parse(props);
  const { t } = useTranslation();

  const { data: majorExpenditureBudgetPurposeData } =
    useExpenditureBudgetPurpose({
      id: majorExpenditureBudgetPurposeId ?? '',
    });

  const majorExpenditureBudgetPurposeName =
    majorExpenditureBudgetPurposeData &&
    getExpenditureBudgetPurposeConcatData({
      data: majorExpenditureBudgetPurposeData,
    }).name;

  return (
    <>
      <ProjectInformationHeader
        ministryId={rest.ministryId}
        historySavedAt={props.historySavedAt}
        organization={rest.organization}
        relatedOrganizations={rest.relatedOrganizations}
        otherResponsibleOrganizations={rest.otherResponsibleOrganizations}
        peopleInCharge={rest.peopleInCharge}
        businessManagers={rest.businessManagers}
        snapshotId={rest.snapshotId}
        pinpointSheetType="RS_SKIPPED_REVIEW"
      />

      <ContentWrapper title={t('basic-information')}>
        <ContentItem borderBottom={0}>
          <ContentItems
            items={[
              <ProjectNumberPinpointItem
                key="project-number"
                projectNumber={projectNumber}
                projectId={id}
                pinpointSheetType="RS_SKIPPED_REVIEW"
              />,
              <ProjectGroupPinpointItem
                key="project-group"
                projectId={id}
                pinpointSheetType="RS_SKIPPED_REVIEW"
              />,
            ]}
          />
        </ContentItem>

        <PublishedProjectMutableGuard>
          <ContentItem paddingTop={12} paddingBottom={0} borderBottom={0}>
            <ContentItems
              items={[
                <PinpointMarker
                  key="skip-administrative-reform-team-evaluation-reason"
                  pinpoint={{
                    path: { project: id },
                    field: 'rs.skippedReview.reason',
                  }}
                >
                  <TitledText
                    title={t(
                      'skip-administrative-reform-team-evaluation-reason',
                    )}
                    text={
                      skipAdministrativeReformTeamEvaluationReason
                        ? SkipAdministrativeReformTeamEvaluationReasonLabelModifier[
                            skipAdministrativeReformTeamEvaluationReason
                          ]
                        : null
                    }
                  />
                </PinpointMarker>,
                <PinpointMarker
                  key="skip-administrative-reform-team-evaluation-reason"
                  pinpoint={{
                    path: { project: id },
                    field: 'rs.skippedReview.reason-supplement',
                  }}
                >
                  <TitledText
                    title={t(
                      'skip-administrative-reform-team-evaluation-reason-supplement',
                    )}
                    text={
                      skipAdministrativeReformTeamEvaluationReasonSupplement
                    }
                  />
                </PinpointMarker>,
              ]}
            />
          </ContentItem>

          <ContentItem>
            <ContentItems
              items={[
                <PinpointMarker
                  key="expenditure-purpose-level-3-1"
                  pinpoint={{
                    path: { project: id },
                    field: 'rs.skippedReview.expenditure-purpose-level-3-1',
                  }}
                >
                  <TitledText
                    title={t('expenditure-purpose-level-3-1')}
                    text={majorExpenditureBudgetPurposeName}
                  />
                </PinpointMarker>,
              ]}
            />
          </ContentItem>
        </PublishedProjectMutableGuard>
        <Flex vertical style={{ marginTop: 24, marginBottom: 4 }}>
          <ProjectSheetTypePinpointItem
            projectId={id}
            isSkipAdministrative={true}
            pinpointSheetType="RS_SKIPPED_REVIEW"
          />
        </Flex>

        <Text variant="h4" text={name} />

        <ContentItem>
          <ContentItems
            mode="fillRight"
            items={[
              <ProjectFiscalYearPinpointItem
                key="fiscal-year"
                projectId={id}
                fiscalYear={fiscalYear}
                pinpointSheetType="RS_SKIPPED_REVIEW"
              />,
              <ProjectCategoryPinpointItem
                key="project-category"
                projectId={id}
                category={category}
                pinpointSheetType="RS_SKIPPED_REVIEW"
              />,
              <ProjectPeriodPinpointItem
                key="period"
                projectId={id}
                startYear={startYear}
                isUnknownStart={isUnknownStart}
                endYear={endYear}
                isNoScheduledEnd={isNoScheduledEnd}
                pinpointSheetType="RS_SKIPPED_REVIEW"
              />,
            ]}
          />
        </ContentItem>

        <ContentItem borderBottom={0} paddingBottom={0}>
          <ProjectPolicyPinpointItem
            projectId={id}
            policiesForProject={policiesForProject}
            pinpointSheetType="RS_SKIPPED_REVIEW"
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper borderBottom={0} title={t('others')}>
        <ContentItem borderBottom={0}>
          <ContentItems
            items={[
              <ProjectOldProjectNumberPinpointItem
                key="old-project-number"
                projectId={id}
                oldProjectNumber={oldProjectNumber}
                pinpointSheetType="RS_SKIPPED_REVIEW"
              />,
              <ProjectOrderNumberPinpointItem
                key="project-order-number"
                projectId={id}
                sortOrder={sortOrder}
                pinpointSheetType="RS_SKIPPED_REVIEW"
              />,
              <ProjectRemarkPinpointItem
                key="remark"
                projectId={id}
                remark={remark}
                pinpointSheetType="RS_SKIPPED_REVIEW"
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>
    </>
  );
};
